// Weekly Owner Snapshot — condensed digest at the top of Owner BI.
// Compares last 7 days vs previous 7 days and previews the next 7 days.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Percent, Users, Calendar,
  Mail, Loader2, RefreshCw, ArrowUpRight, Info,
} from 'lucide-react';
import { apiCall } from './_shared';
import { BarRevenueCard } from './bar/BarRevenueCard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const eur = (n) => `€${Number(n || 0).toLocaleString('en-EU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const flag = (cc) => {
  if (!cc || cc.length !== 2 || cc === '??') return '🏳️';
  const A = 0x1F1E6;
  return String.fromCodePoint(...cc.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65)));
};

const DeltaChip = ({ pct, invert }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const isUp = pct > 0;
  const good = invert ? !isUp : isUp;  // for "occupancy up" is good, "expense up" is bad — here everything is good=up
  const cls = pct === 0
    ? 'text-slate-400 bg-slate-700'
    : good ? 'text-green-300 bg-green-900/40 border border-green-600/40' : 'text-red-300 bg-red-900/40 border border-red-600/40';
  const Icon = pct === 0 ? Info : (isUp ? TrendingUp : TrendingDown);
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold ${cls}`}>
      <Icon className="w-3 h-3" />{isUp ? '+' : ''}{pct}%
    </span>
  );
};

const MicroBar = ({ pct }) => {
  const w = Math.min(100, Math.max(0, pct));
  const c = pct >= 80 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : pct >= 30 ? 'bg-green-500' : 'bg-blue-400';
  return (
    <div className="w-full bg-slate-700 rounded h-1.5 overflow-hidden">
      <div className={`${c} h-1.5 rounded`} style={{ width: `${w}%` }} />
    </div>
  );
};

const WeeklySnapshot = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/bi/weekly-snapshot`, {
        credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to load');
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const last = data?.last_week?.kpis || {};
  const prev = data?.prev_week?.kpis || {};
  const nextWk = data?.next_week?.kpis || {};
  const nextForecast = data?.next_week?.forecast || [];
  const deltas = data?.deltas || {};
  const topSources = data?.top_sources || [];
  const topCountries = data?.top_countries || [];
  const topRooms = data?.top_rooms || [];

  const forecastMini = useMemo(() => nextForecast.map((f) => {
    const d = new Date(f.date + 'T00:00:00');
    return (
      <div key={f.date} className="flex-1 min-w-0 flex flex-col items-center gap-1" data-testid={`snapshot-forecast-${f.date}`}>
        <span className="text-[10px] text-slate-400 uppercase font-semibold">
          {d.toLocaleDateString('en-GB', { weekday: 'short' })}
        </span>
        <div className="w-full"><MicroBar pct={f.occupancy_pct} /></div>
        <span className="text-xs font-bold text-slate-200">{f.occupancy_pct}%</span>
      </div>
    );
  }), [nextForecast]);

  const sourceRows = useMemo(() => topSources.map((s, i) => (
    <div key={s.source} className="flex items-center gap-3 text-sm py-1" data-testid={`snapshot-source-${s.source}`}>
      <span className="w-5 text-xs text-slate-500 font-bold">#{i + 1}</span>
      <span className="flex-1 font-medium text-slate-100 truncate">{s.source}</span>
      <span className="text-xs text-slate-400">{s.bookings}</span>
      <span className="text-sm font-semibold text-orange-400 w-20 text-right">{eur(s.revenue)}</span>
    </div>
  )), [topSources]);

  const countryRows = useMemo(() => topCountries.map((c, i) => (
    <div key={c.country} className="flex items-center gap-3 text-sm py-1" data-testid={`snapshot-country-${c.country}`}>
      <span className="w-5 text-xs text-slate-500 font-bold">#{i + 1}</span>
      <span className="text-xl">{flag(c.country)}</span>
      <span className="flex-1 font-medium text-slate-100">{c.country}</span>
      <span className="text-sm font-semibold text-blue-400">{c.bookings}</span>
    </div>
  )), [topCountries]);

  const roomRows = useMemo(() => topRooms.map((r, i) => (
    <div key={r.room_id} className="flex items-center gap-3 text-sm py-1" data-testid={`snapshot-room-${r.room_id}`}>
      <span className="w-5 text-xs text-slate-500 font-bold">#{i + 1}</span>
      <span className="flex-1 font-medium text-slate-100 truncate">{r.room_name}</span>
      <span className="text-xs text-slate-400 w-14 text-right">{r.nights_sold}n</span>
      <span className="text-sm font-semibold text-orange-400 w-20 text-right">{eur(r.revenue)}</span>
    </div>
  )), [topRooms]);

  const emailDigest = async () => {
    const recipient = window.prompt("Send this week's digest to which email?\n(Leave empty to use OWNER_DIGEST_EMAIL from server env)");
    if (recipient === null) return;
    try {
      const qs = recipient ? `?recipient=${encodeURIComponent(recipient)}` : '';
      const json = await apiCall(`/api/admin/digest/send-now${qs}`, { method: 'POST' });
      alert(`Sent ✓ to ${json.recipient} (id: ${json.email_id || '—'})`);
    } catch (e) { alert('Send failed: ' + e.message); }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 shadow-lg text-white overflow-hidden relative" data-testid="weekly-snapshot">
      {/* Decorative gradient blob */}
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-orange-500 opacity-10 rounded-full blur-3xl" aria-hidden="true" />

      <div className="flex flex-wrap justify-between items-center gap-3 mb-5 relative">
        <div>
          <div className="flex items-center gap-2 text-xs text-orange-300 uppercase tracking-wider font-semibold mb-1">
            <Calendar className="w-3.5 h-3.5" /> Weekly Snapshot
          </div>
          <h3 className="text-xl font-bold">Last 7 days at a glance</h3>
          {data && (
            <p className="text-xs text-slate-400 mt-1">
              {data.last_week?.start_date} → {data.last_week?.end_date} · vs. previous week
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={emailDigest}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 px-3 py-1.5 rounded text-xs text-slate-200"
            data-testid="snapshot-email-btn"
          >
            <Mail className="w-3.5 h-3.5" /> Email this
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded text-xs text-white font-semibold"
            data-testid="snapshot-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-500/20 border border-red-500/40 text-red-200 rounded-lg p-3 text-sm mb-4">{err}</div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 relative">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid="snapshot-kpi-revenue">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Revenue</span>
            <DeltaChip pct={deltas.revenue_pct} />
          </div>
          <p className="text-2xl font-bold text-white">{eur(last.revenue)}</p>
          <p className="text-[11px] text-slate-500 mt-1">prev: {eur(prev.revenue)}</p>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid="snapshot-kpi-occupancy">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Occupancy</span>
            <DeltaChip pct={deltas.occupancy_pct} />
          </div>
          <p className="text-2xl font-bold text-white">{last.occupancy_pct || 0}%</p>
          <p className="text-[11px] text-slate-500 mt-1">prev: {prev.occupancy_pct || 0}%</p>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid="snapshot-kpi-adr">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">ADR</span>
            <DeltaChip pct={deltas.adr_pct} />
          </div>
          <p className="text-2xl font-bold text-white">{eur(last.adr)}</p>
          <p className="text-[11px] text-slate-500 mt-1">prev: {eur(prev.adr)}</p>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid="snapshot-kpi-revpar">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">RevPAR</span>
            <DeltaChip pct={deltas.revpar_pct} />
          </div>
          <p className="text-2xl font-bold text-white">{eur(last.revpar)}</p>
          <p className="text-[11px] text-slate-500 mt-1">prev: {eur(prev.revpar)}</p>
        </div>
      </div>

      {/* Top-3 columns */}
      <div className="grid md:grid-cols-3 gap-4 mb-6 relative">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Top sources</p>
          {sourceRows.length ? sourceRows : <p className="text-xs text-slate-500">No source data</p>}
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Top countries</p>
          {countryRows.length ? countryRows : <p className="text-xs text-slate-500">No country data</p>}
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Top rooms</p>
          {roomRows.length ? roomRows : <p className="text-xs text-slate-500">No room data</p>}
        </div>
      </div>

      {/* Next 7-day mini-forecast */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 relative">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" /> Next 7 days forecast
          </p>
          <p className="text-xs text-slate-400">
            {nextWk.booked_room_nights || 0} room-nights · {eur(nextWk.revenue)} expected
          </p>
        </div>
        <div className="flex gap-2">
          {forecastMini.length ? forecastMini : <p className="text-xs text-slate-500">No forecast</p>}
        </div>
      </div>
      <BarRevenueCard bar={data?.bar} title="Bar (last 7 days)" />
    </div>
  );
};

export default WeeklySnapshot;
